import type { JsonSchema7, VerticalLayout } from '@jsonforms/core';
import type { Conditions } from '@risksmart-app/form-configuration/src/field-types/types';
import type { GetFormCustomisationQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createContext, useContext } from 'react';

import {
  createFormFieldElementsStore,
  type FormFieldElementsStore,
} from '../stores/formFieldElementsStore';
import {
  createFormFieldRegistryStore,
  type FormFieldRegistryStore,
} from '../stores/formFieldRegistryStore';

export type TypedFormFieldConfiguration = Omit<
  GetFormCustomisationQuery['form_field_configuration'][number],
  'Conditions'
> & {
  Conditions?: Conditions | null | undefined;
};

export type CustomisableFormDataContextState = {
  formFieldConfigurations: TypedFormFieldConfiguration[] | null;
  formFieldOrdering: GetFormCustomisationQuery['form_field_ordering'] | null;
  formSections: GetFormCustomisationQuery['form_section_configuration'] | null;
  customAttributeSchema: {
    UiSchema: VerticalLayout;
    Schema: JsonSchema7;
    Id: string;
  } | null;
  fieldRegistryStore: FormFieldRegistryStore;
  fieldElementsStore: FormFieldElementsStore;
};

// Default store handles are inert fallbacks for consumers rendered outside a
// CustomisableFormDataProvider (e.g. standalone Controlled* field components in
// isolation). The only writers (CustomisableFieldWrapper) always sit under a
// provider, so these defaults never accumulate cross-form state — each real
// form instance gets its own pair via the provider's useState initialiser.
export const CustomisableFormDataContext =
  createContext<CustomisableFormDataContextState>({
    formFieldConfigurations: null,
    formFieldOrdering: null,
    formSections: null,
    customAttributeSchema: null,
    fieldRegistryStore: createFormFieldRegistryStore(),
    fieldElementsStore: createFormFieldElementsStore(),
  });

export const useCustomisableFormDataContext = () =>
  useContext(CustomisableFormDataContext);
